import React, { useCallback, useEffect, useState } from "react";
import { formatCompact } from "../userFloorHelpers";
import { fetchAdminUserPredictions } from "../api/fetchAdminUserPredictions";
import {
  predictionContextLine,
  predictionMatchInitials,
  predictionMetricsLine,
  predictionSummaryParts,
  predictionTimingMeta,
} from "../userPredictionHelpers";
import UserActivitySurface from "./UserActivitySurface.jsx";
import UserSurfaceRow from "./UserSurfaceRow.jsx";
import { SURFACE_PAGE_SIZE } from "./UserSurfacePager.jsx";
import { surfacePageRange } from "./userSurfacePageHelpers";

export default function UserPredictionsTab({ userId }) {
  const [predictions, setPredictions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: SURFACE_PAGE_SIZE, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(
    async ({ page = 1, refresh = false } = {}) => {
      if (!userId) return;
      setLoading(true);
      setError(null);
      try {
        const data = await fetchAdminUserPredictions(userId, { page, limit: SURFACE_PAGE_SIZE, refresh });
        setPredictions(data?.predictions || []);
        setSummary(data?.summary || null);
        setPagination((prev) => ({
          ...prev,
          ...(data?.pagination || {}),
          limit: data?.pagination?.limit || prev.limit,
        }));
      } catch (err) {
        setPredictions([]);
        setSummary(null);
        setError(err.response?.data?.error || err.message || "Failed to load predictions");
      } finally {
        setLoading(false);
      }
    },
    [userId]
  );

  useEffect(() => {
    load({ page: 1 });
  }, [userId, load]);

  const { page, totalPages, total, limit } = pagination;
  const { startIndex, endIndex } = surfacePageRange(page, limit, predictions.length, total);

  return (
    <UserActivitySurface
      title="Community tips"
      total={summary?.total ?? total ?? predictions.length}
      totalLabel="tips"
      summaryParts={predictionSummaryParts(summary)}
      loading={loading}
      error={error}
      onRefresh={() => load({ page, refresh: true })}
      onRetry={() => load({ page })}
      emptyMessage="No predictions posted by this member."
      itemCount={predictions.length}
      pager={{
        page,
        totalPages,
        total,
        startIndex,
        endIndex,
        loading,
        onPageChange: (nextPage) => load({ page: nextPage }),
        emptyLabel: "No tips",
      }}
    >
      {predictions.map((prediction, idx) => {
        const timing = predictionTimingMeta(prediction.timing);
        const tags = [{ label: timing.label, tone: timing.tone }];
        if (prediction.isPremium) tags.push({ label: "Premium", tone: "live" });
        return (
          <UserSurfaceRow
            key={prediction._id}
            rank={startIndex + idx + 1}
            initials={predictionMatchInitials(prediction.matchHome, prediction.matchAway)}
            name={prediction.matchLabel || "Match"}
            tags={tags}
            context={predictionContextLine(prediction)}
            metrics={predictionMetricsLine(prediction, formatCompact)}
          />
        );
      })}
    </UserActivitySurface>
  );
}
